/**
 * Cómo se identifica un CLIENTE en el resultado del optimizador.
 *
 * Existe porque la regla estaba copiada en siete lugares de cinco archivos
 * (`commercialName || name || fallback`), y agregarle el código habría sido aplicar
 * el mismo cambio siete veces. Con la regla duplicada, la primera corrección futura
 * las hace divergir sin dar error: el síntoma sería el mismo cliente escrito de dos
 * formas en dos listas de la misma pantalla.
 *
 * Dos datos, dos propósitos, y por eso no se elige uno:
 *
 * - el **código** + el **nombre legal** es lo que cruza con `master/routing/sub-route`
 *   y con el ERP. El código se pega tal cual allá, así que conserva sus ceros a la
 *   izquierda: recortarlos daría un número más limpio que ya no sirve para pegar,
 *   que es justamente para lo que se muestra.
 * - el **nombre comercial** es el que se reconoce en la calle. Antes REEMPLAZABA al
 *   legal, así que el mismo cliente se leía distinto acá y en el maestro.
 *
 * Módulo puro: entra el `shipTo` de la orden, sale el modelo de vista. Sin HTTP.
 */

/**
 * Separador entre el código y el nombre.
 *
 * Raya larga, que es lo que ESTA pantalla ya usa en sus selectores de oficina y de
 * territorio. El maestro usa guion (` - `); la diferencia es conocida y deliberada:
 * la consistencia que el operador percibe es la de la pantalla que tiene delante, y
 * alinear los dos selectores existentes con otra pantalla sería un cambio más grande
 * y sin pedido.
 */
export const CLIENT_CODE_SEPARATOR = ' — ';

/** Lo mínimo del cliente que hace falta para identificarlo. */
export interface ClientLike {
  name?: string;
  commercialName?: string;
  code?: string;
  externalKeys?: { code?: string };
}

/** Cómo se muestra un cliente: el par identificador, y su nombre comercial. */
export interface ClientLabel {
  /** `CÓDIGO — NOMBRE LEGAL`; solo el nombre sin código; o el respaldo. */
  name: string;
  /**
   * Nombre comercial, **solo** si existe y dice algo distinto del legal. Se omite
   * cuando coincide: mostrar el mismo texto dos veces es ruido con forma de dato.
   */
  commercialName?: string;
}

/**
 * Código del cliente, con la misma cascada que `getCode()` del repo.
 *
 * `externalKeys.code` es el del ERP y el que el maestro muestra; `code` es el
 * respaldo para los modelos que lo traen plano.
 */
function codeOf(client: ClientLike | undefined): string {
  const code = client?.externalKeys?.code ?? client?.code;
  return code === undefined || code === null ? '' : String(code).trim();
}

function textOf(value: string | undefined): string {
  return value === undefined || value === null ? '' : String(value).trim();
}

/**
 * Identifica un cliente para mostrarlo.
 *
 * El código se omite **junto con su separador** cuando el cliente no lo declara: un
 * ` — NOMBRE` con el separador huérfano se lee como un dato faltante donde no falta
 * nada.
 *
 * @param client `shipTo` de la orden, o el socio de negocio.
 * @param fallback qué mostrar cuando no hay ningún nombre — el identificador del
 *   pedido. Sin él, una parada sin cliente resoluble quedaría sin rótulo.
 */
export function clientLabel(client: ClientLike | undefined, fallback: string): ClientLabel {
  const legal = textOf(client?.name);
  const commercial = textOf(client?.commercialName);
  const code = codeOf(client);

  // Sin nombre legal el código solo no identifica a nadie ante el operador: se usa
  // el comercial si está, y el respaldo si tampoco.
  const base = legal || commercial || textOf(fallback);
  const name = code && base ? `${code}${CLIENT_CODE_SEPARATOR}${base}` : base;

  return {
    name,
    // Si el comercial ya es el nombre mostrado —porque no había legal— no se repite.
    commercialName: commercial && commercial !== base ? commercial : undefined,
  };
}
